// src/services/api.ts
// Вспомогательные функции для безопасной работы с глобальными объектами

// Определение необходимых типов, если DOM lib не подключена
interface Window {
  localStorage?: Storage;
}

declare const window: Window | undefined;

// Типы для fetch API, если они не доступны
type HeadersInit = Record<string, string> | string[][] | Headers;

// Используем существующие типы Vite для import.meta.env
// Объявления уже есть в node_modules/vite/types/importMeta.d.ts

// Определение базовый URL в зависимости от окружения
const isDevelopment = import.meta.env.DEV;
// В режиме разработки используем локальный backend, в продакшене - реальный API
const BASE_URL = isDevelopment ? 'http://192.168.1.76:8000' : 'https://api.pinnax.app';

console.log('API Base URL:', BASE_URL, 'Environment:', isDevelopment ? 'development' : 'production');

// Интерфейс для ответа при обновлении токена
interface RefreshResponse {
  access: string;
  refresh?: string;
}

// Безопасное получение localStorage
const getLocalStorage = (): Storage | null => {
  if (typeof window !== 'undefined' && window?.localStorage) {
    return window.localStorage;
  }
  return null;
};

// Простая реализация HTTP клиента с использованием fetch API
const createFetchClient = () => {
  const getAuthHeader = (): Record<string, string> => {
    const storage = getLocalStorage();
    const token = storage ? storage.getItem('accessToken') : null;
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const handleResponse = async <T>(response: Response): Promise<T> => {
    if (!response.ok) {
      // Обработка 401 ошибки и обновление токена
      if (response.status === 401) {
        const storage = getLocalStorage();
        const refreshToken = storage ? storage.getItem('refreshToken') : null;
        
        if (refreshToken) {
          try {
            const refreshResponse = await fetch(`${BASE_URL}/api/token/refresh/`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ refresh: refreshToken })
            });
            
            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json() as RefreshResponse;
              if (refreshData.access) {
                storage?.setItem('accessToken', refreshData.access);
                // Повторяем оригинальный запрос с новым токеном
                const retryResponse = await fetch(response.url, {
                  method: response.type,
                  headers: {
                    ...Object.fromEntries(response.headers.entries()),
                    'Authorization': `Bearer ${refreshData.access}`
                  }
                });
                return handleResponse(retryResponse);
              }
            }
            
            // Если обновление не удалось, очищаем токены
            storage?.removeItem('accessToken');
            storage?.removeItem('refreshToken');
          } catch (error) {
            console.error('Token refresh failed:', error);
          }
        }
      }
      
      // Получаем текст ошибки или сообщение из JSON
      let errorMessage = 'Ошибка запроса';
      try {
        const errorData = await response.json();
        errorMessage = (errorData as any).detail || (errorData as any).message || JSON.stringify(errorData);
      } catch (e) {
        try {
          errorMessage = await response.text();
        } catch (textError) {
          errorMessage = `HTTP error: ${response.status}`;
        }
      }
      
      throw new Error(errorMessage);
    }
    
    // Для успешных ответов
    try {
      return await response.json() as T;
    } catch (e) {
      // Если ответ не JSON
      return await response.text() as unknown as T;
    }
  };

  // HTTP методы
  return {
    get: async <T = any>(url: string, params?: Record<string, string>): Promise<T> => {
      const queryParams = params ? `?${new URLSearchParams(params).toString()}` : '';
      const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}${queryParams}`;
      
      try {
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        };
        
        const response = await fetch(fullUrl, {
          method: 'GET',
          headers
        });
        
        return handleResponse<T>(response);
      } catch (error) {
        console.error(`GET request to ${url} failed:`, error);
        throw error;
      }
    },
    
    post: async <T = any>(url: string, data?: any, params?: Record<string, string>): Promise<T> => {
      const queryParams = params ? `?${new URLSearchParams(params).toString()}` : '';
      const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}${queryParams}`;
      
      try {
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        };
        
        const response = await fetch(fullUrl, {
          method: 'POST',
          headers,
          body: data ? JSON.stringify(data) : undefined
        });
        
        return handleResponse<T>(response);
      } catch (error) {
        console.error(`POST request to ${url} failed:`, error);
        throw error;
      }
    },
    
    put: async <T = any>(url: string, data?: any): Promise<T> => {
      const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`;
      
      try {
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        };
        
        const response = await fetch(fullUrl, {
          method: 'PUT',
          headers,
          body: data ? JSON.stringify(data) : undefined
        });
        
        return handleResponse<T>(response);
      } catch (error) {
        console.error(`PUT request to ${url} failed:`, error);
        throw error;
      }
    },
    
    patch: async <T = any>(url: string, data?: any): Promise<T> => {
      const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`;
      
      try {
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        };
        
        const response = await fetch(fullUrl, {
          method: 'PATCH',
          headers,
          body: data ? JSON.stringify(data) : undefined
        });
        
        return handleResponse<T>(response);
      } catch (error) {
        console.error(`PATCH request to ${url} failed:`, error);
        throw error;
      }
    },
    
    delete: async <T = any>(url: string): Promise<T> => {
      const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`;
      
      try {
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        };
        
        const response = await fetch(fullUrl, {
          method: 'DELETE',
          headers
        });
        
        return handleResponse<T>(response);
      } catch (error) {
        console.error(`DELETE request to ${url} failed:`, error);
        throw error;
      }
    }
  };
};

// Создаем клиент
const httpClient = createFetchClient();

// Интерфейс для типизации API
export interface ApiInterface {
  get: <T = any>(url: string, params?: Record<string, string>) => Promise<T>;
  post: <T = any>(url: string, data?: any, params?: Record<string, string>) => Promise<T>;
  put: <T = any>(url: string, data?: any) => Promise<T>;
  patch: <T = any>(url: string, data?: any) => Promise<T>;
  delete: <T = any>(url: string) => Promise<T>;
  endpoints: {
    auth: {
      login: string;
      register: string;
      profile: string;
      me: string; // Добавляем для совместимости
      refreshToken: string;
      changePassword: string; // Добавленный эндпоинт для смены пароля
      updateProfile: string; //  Добавленный эндпоинт для сохранения настроек приложения
    };
    social: {
      facebook: string;
      google: string;
      listAccounts: string;
      unlinkAccount: string;
    };
    dashboard: { // Добавляем dashboard в интерфейс
      analytics: string;
      businessStatistics: string;
    };
    pricing?: {
      plans: string;
    };
    logs?: {
      errors: string;
    };
  };
}

export const api: ApiInterface = {
  get: httpClient.get,
  post: httpClient.post,
  put: httpClient.put,
  patch: httpClient.patch,
  delete: httpClient.delete,
  
  endpoints: {
    auth: {
      login: '/api/token/', // Для JWT токена из Django REST framework JWT
      register: '/api/business/', // Правильный эндпоинт для регистрации бизнеса
      profile: '/api/business/profile/', // Для получения профиля пользователя
      me: '/api/business/profile/', // Дублируем для обратной совместимости
      refreshToken: '/api/token/refresh/', // Для обновления JWT токена
      changePassword: '/api/business/:id/change-password/', // Новый эндпоинт для смены пароля
      updateProfile: '/api/business/profile/update/' // Новый эндпоинт
    },
    social: {
      facebook: '/api/accounts/facebook/login/',
      google: '/api/accounts/google/login/',
      listAccounts: '/api/accounts/social/',
      unlinkAccount: '/api/accounts/unlink/'
    },
    dashboard: {
      analytics: '/api/analytics/clients/', // Общая аналитика клиентов
      businessStatistics: '/api/business/:id/statistics/' // Статистика по бизнесу
    },
    pricing: {
      plans: '/api/pricing/plans/',
    },
    logs: {
      errors: '/api/error-logs'
    }
  }
};

export default api;